import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Input,
  NumberInputFieldProps,
  NumberInputProps,
  Text,
  useBoolean
} from '@chakra-ui/react'
import { useRef } from 'react'
import { Controller, FieldValues, useFormContext } from 'react-hook-form'
import { NumberFormatValues, NumericFormat } from 'react-number-format'
import { NUMBER_FORM_MAX_VALUE } from 'src/constants'
import { AttentionIcon } from '../Icons/AttentionIcon'
import { CommonFormLabel } from './CommonFormLabel'
import { FieldInputProps, getErrorMessage } from './FormTextInput'

export type FormPriceAmountInputType<T extends FieldValues> = FieldInputProps<
  T,
  number
> &
  Omit<NumberInputFieldProps, 'value'> & {
    isDisabled?: boolean
  } & {
    variant?: NumberInputProps['variant']
    onChange?: (value: number) => void
  }

export const FormPriceAmountInput = <T extends FieldValues = never>({
  name,
  isRequired,
  label,
  helperText,
  helpTooltipText,
  showErrorText = true,
  errorMessage,
  formControlWidth,
  labelIndexNumber,
  w,
  isDisabled,
  variant,
  placeholder,
  ...inputProps
}: FormPriceAmountInputType<T>): ReturnType<React.VFC> => {
  const [isIMENotValid, setIMENotValid] = useBoolean()
  const prevInputValue = useRef<string>('')
  const prev2InputValue = useRef<string>('')
  const { control } = useFormContext()

  const _errorMessage = getErrorMessage(name) || errorMessage

  return (
    <FormControl
      isRequired={isRequired}
      isInvalid={_errorMessage}
      w={formControlWidth || 'auto'}
      // labelが長くて2行以上になるときにラベルとフォームの間隔を自動で調節したい
      justifyContent="space-between"
      flexDir="column"
      display="flex"
    >
      <CommonFormLabel
        label={label}
        labelIndexNumber={labelIndexNumber}
        helpTooltipText={helpTooltipText}
        isRequired={isRequired}
      />
      <Controller
        render={({ field: { onChange, onBlur, value: controllerValue } }) => {
          return (
            <Input
              w={w}
              placeholder={placeholder}
              as={NumericFormat}
              prefix="￥"
              thousandSeparator
              decimalSeparator="."
              isAllowed={(values: NumberFormatValues) => {
                const { value, floatValue } = values
                return (
                  value === '' ||
                  value === '-' ||
                  (floatValue ?? 0) < NUMBER_FORM_MAX_VALUE
                )
              }}
              onValueChange={({ floatValue }: NumberFormatValues) => {
                if (floatValue === undefined) {
                  return onChange(0)
                }
                // PostgreSQLのnumberの上限値2147483647を超えないようにする
                if (floatValue > 2147483647) {
                  inputProps.onChange?.(2147483647)
                  return onChange(2147483647)
                }
                if (isNaN(floatValue)) {
                  inputProps.onChange?.(0)
                  onChange(0)
                } else {
                  inputProps.onChange?.(floatValue)
                  onChange(floatValue)
                }
              }}
              value={controllerValue}
              onChange={(e) => {
                // TODO: 本当は全角入力で数字が入ってもフォーカス外した時点で半角数字に直すみたいな処理を入れたいが、
                // 時間がないのでできていない
                // 現時点では、ライブラリが全角入力をすべて弾いてしまう
                // 全角入力をしているっぽいときは警告を出す
                // 全角入力を検知する方法は現時点でないため、同じ入力が続いている場合全角だと推定する
                const { value } = e.target
                if (
                  prev2InputValue.current === prevInputValue.current &&
                  prevInputValue.current === value
                ) {
                  setIMENotValid.on()
                } else if (isIMENotValid) {
                  setIMENotValid.off()
                }
                prev2InputValue.current = prevInputValue.current
                prevInputValue.current = value
              }}
              onBlur={(e) => {
                inputProps.onBlur?.(e)
                onBlur()
              }}
              isDisabled={isDisabled}
              variant={variant}
            />
          )
        }}
        control={control}
        name={name}
      />

      {isIMENotValid && (
        <Text
          fontSize="xs"
          fontWeight={500}
          lineHeight={1}
          mt="2"
          color="primary.alert"
        >
          入力が受け付けられません。
          <br />
          全角かな入力になっている可能性があります
        </Text>
      )}

      {showErrorText && (
        <FormErrorMessage>
          <AttentionIcon />
          {_errorMessage}
        </FormErrorMessage>
      )}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
}
